/**
 * SECURE TOKEN APPROVAL UTILITIES
 *
 * CRITICAL SECURITY FIX: Eliminates unlimited token approvals (maxUint256)
 * that could lead to complete fund loss if contracts are compromised.
 *
 * APPROVAL STRATEGIES:
 * - EXACT: Approve only the exact amount needed (recommended)
 * - BUFFERED: Approve exact amount + small buffer for gas/precision
 * - REASONABLE_MAX: Approve a reasonable maximum (e.g., 100x the amount)
 * - UNLIMITED: maxUint256 (HIGH RISK - not recommended)
 *
 * SECURITY PRINCIPLES:
 * 1. Never approve more than necessary
 * 2. Prefer exact amount approvals
 * 3. Use buffers only when absolutely needed
 * 4. Revoke approvals when no longer needed
 * 5. Monitor contract trustworthiness
 */

import { Contract } from 'web3-eth-contract';
import { AbiItem } from 'web3-utils';
import { web3 } from '../web3';

export enum ApprovalStrategy {
  EXACT = 'exact', // Approve exact amount needed (RECOMMENDED)
  BUFFERED = 'buffered', // Exact amount + 10% buffer
  REASONABLE_MAX = 'reasonable_max', // Reasonable maximum (100x amount)
  UNLIMITED = 'unlimited', // maxUint256 (HIGH RISK - AVOID)
}

export interface ApprovalOptions {
  strategy: ApprovalStrategy;
  bufferPercentage?: number; // For BUFFERED strategy (default: 10%)
  maxMultiplier?: number; // For REASONABLE_MAX strategy (default: 100x)
  requireUserConfirmation?: boolean; // Show warning for high-risk approvals
}

/**
 * Calculate secure approval amount based on strategy
 *
 * @param requiredAmount Amount actually needed for the transaction (in wei)
 * @param options Approval strategy options
 * @returns Approval amount in wei and security warning if applicable
 */
export function calculateSecureApprovalAmount(
  requiredAmount: string,
  options: ApprovalOptions
): { amount: string; warning?: string; risk: 'low' | 'medium' | 'high' } {
  const requiredAmountBigInt = BigInt(requiredAmount);

  switch (options.strategy) {
    case ApprovalStrategy.EXACT:
      return {
        amount: requiredAmount,
        risk: 'low',
      };

    case ApprovalStrategy.BUFFERED: {
      const bufferPercent = options.bufferPercentage || 10;
      const bufferMultiplier = BigInt(Math.floor((100 + bufferPercent) * 100));
      const denominator = BigInt(10000);
      const bufferedAmount = (requiredAmountBigInt * bufferMultiplier) / denominator;
      return {
        amount: bufferedAmount.toString(),
        warning: `Approving ${bufferPercent}% more than needed for gas/precision safety.`,
        risk: 'low',
      };
    }

    case ApprovalStrategy.REASONABLE_MAX: {
      const multiplier = options.maxMultiplier || 100;
      const maxAmount = requiredAmountBigInt * BigInt(multiplier);
      return {
        amount: maxAmount.toString(),
        warning: `Approving ${multiplier}x the required amount. Consider using exact approval for maximum security.`,
        risk: 'medium',
      };
    }

    case ApprovalStrategy.UNLIMITED: {
      const maxUint256 =
        '115792089237316195423570985008687907853269984665640564039457584007913129639935';
      return {
        amount: maxUint256,
        warning: 'UNLIMITED APPROVAL: Contract can spend all your tokens. EXTREMELY HIGH RISK!',
        risk: 'high',
      };
    }

    default:
      throw new Error(`Unknown approval strategy: ${options.strategy}`);
  }
}

/**
 * Check if existing allowance covers the required amount
 *
 * @param tokenContract Token contract instance
 * @param owner Owner address
 * @param spender Spender address
 * @param requiredAmount Required amount in wei
 * @returns True if allowance is sufficient
 */
export async function hasSufficientAllowance(
  tokenContract: Contract<AbiItem[]>,
  owner: string,
  spender: string,
  requiredAmount: string
): Promise<boolean> {
  try {
    const allowance = await tokenContract.methods.allowance(owner, spender).call();

    // Handle different return types from contract call
    let allowanceString: string;
    if (typeof allowance === 'string') {
      allowanceString = allowance;
    } else if (typeof allowance === 'number') {
      allowanceString = (allowance as number).toString();
    } else if (allowance !== null && allowance !== undefined && typeof allowance === 'object') {
      // Check if it has a toString method
      if ('toString' in allowance && typeof allowance.toString === 'function') {
        allowanceString = allowance.toString();
      } else {
        return false;
      }
    } else {
      return false;
    }

    const allowanceBigInt = BigInt(allowanceString);
    const requiredBigInt = BigInt(requiredAmount);
    return allowanceBigInt >= requiredBigInt;
  } catch (error) {
    return false;
  }
}

/**
 * Execute secure token approval with appropriate strategy
 *
 * @param tokenContract Token contract instance
 * @param spenderAddress Address to approve spending for
 * @param requiredAmount Amount needed for transaction (in wei)
 * @param options Approval strategy options
 * @param fromAddress Owner address
 * @returns Promise resolving to transaction result
 */
export function executeSecureApproval(
  tokenContract: Contract<AbiItem[]>,
  spenderAddress: string,
  requiredAmount: string,
  options: ApprovalOptions,
  fromAddress: string
) {
  const { amount, warning, risk } = calculateSecureApprovalAmount(requiredAmount, options);

  // Show security warning for high-risk approvals
  if (warning && options.requireUserConfirmation !== false) {
    if (risk === 'high') {
      const confirmed = confirm(
        `⚠️ SECURITY WARNING: ${warning}\n\n` +
          `This will allow the contract to spend UNLIMITED amounts of your tokens.\n\n` +
          `Are you absolutely sure you want to proceed?`
      );
      if (!confirmed) {
        throw new Error('User cancelled high-risk approval');
      }
    } else if (risk === 'medium' && warning) {
      console.warn('Medium risk approval with warning:', warning);
    }
  }

  // Return the transaction object that can be used with .send().on() pattern
  return tokenContract.methods.approve(spenderAddress, amount).send({ from: fromAddress });
}

/**
 * Get recommended approval strategy based on use case
 *
 * @param useCase The intended use case for the approval
 * @returns Recommended approval strategy
 */
export function getRecommendedApprovalStrategy(useCase: string): ApprovalStrategy {
  switch (useCase.toLowerCase()) {
    case 'reward':
    case 'yield':
    case 'distribution':
      return ApprovalStrategy.BUFFERED; // Small buffer for gas fees

    case 'investment':
    case 'purchase':
      return ApprovalStrategy.EXACT; // Exact amount for purchases

    case 'staking':
    case 'liquidity':
      return ApprovalStrategy.REASONABLE_MAX; // Reasonable max for ongoing operations

    default:
      return ApprovalStrategy.EXACT; // Default to most secure
  }
}

/**
 * Estimate gas cost for approval transaction
 * Useful for calculating buffer amounts
 *
 * @param tokenContract Token contract instance
 * @param spenderAddress Spender address
 * @param amount Amount to approve
 * @param fromAddress Owner address
 * @returns Estimated gas cost in wei
 */
export async function estimateApprovalGasCost(
  tokenContract: Contract<AbiItem[]>,
  spenderAddress: string,
  amount: string,
  fromAddress: string
): Promise<string> {
  try {
    const gasEstimate = await tokenContract.methods
      .approve(spenderAddress, amount)
      .estimateGas({ from: fromAddress });

    const gasPrice = await web3.eth.getGasPrice();
    return (BigInt(gasEstimate) * BigInt(gasPrice)).toString();
  } catch (error) {
    // Return a reasonable default gas cost estimate
    return web3.utils.toWei('0.01', 'ether'); // 0.01 ETH estimate
  }
}

/**
 * Validate approval amount safety
 *
 * @param approvalAmount Amount being approved
 * @param userBalance User's token balance
 * @param requiredAmount Amount actually needed
 * @returns Validation result with warnings
 */
export function validateApprovalSafety(
  approvalAmount: string,
  userBalance: string,
  requiredAmount: string
): { isSafe: boolean; warnings: string[] } {
  const warnings: string[] = [];
  const approvalBigInt = BigInt(approvalAmount);
  const balanceBigInt = BigInt(userBalance);
  const requiredBigInt = BigInt(requiredAmount);

  // Check if approval exceeds balance
  if (approvalBigInt > balanceBigInt) {
    warnings.push('Approval amount exceeds your token balance');
  }

  // Check for excessive approval (more than 10x required)
  const tenTimesRequired = requiredBigInt * BigInt(10);
  if (approvalBigInt > tenTimesRequired) {
    warnings.push('Approval amount is more than 10x the required amount');
  }

  // Check for unlimited approval
  const maxUint256 = BigInt(
    '115792089237316195423570985008687907853269984665640564039457584007913129639935'
  );
  if (approvalBigInt === maxUint256) {
    warnings.push('UNLIMITED APPROVAL: Contract can spend all your tokens forever');
  }

  return {
    isSafe: warnings.length === 0,
    warnings,
  };
}
